import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
  Grid2,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "../../components/ui/LoadingButton";
import { EstablishmentAutocomplete } from "../../components/ui/EstablishmentAutocomplete";
import Input from "../../components/ui/Input";
import { PageHeader } from "../../components/PageHeader";
import { toast } from "react-toastify";
import api from "../../services/api";
import { useIntl } from "../../contexts/IntlContext";
import MetricChartCard from "../../components/Dashboard/MetricChartCard";
import DatePickerField from "../../components/Dashboard/DatePickerField";
import { AuthContext } from "../../contexts/AuthContext";
import { EUserRole } from "../../types/enums";
import { hasOperationalAccess } from "../../utils/authRole";
import { AnalyticsTable } from "./AnalyticsTable";

import { Establishment } from "../../types/establishment";

interface MetricInfo {
  hasChannel: boolean;
}

type ReportData = Array<{
  channelId: string;
  channelName: string;
  establishmentName: string;
  country: string;
  state: string;
  city: string;
  metric: string;
  data: [string[], number[]];
}>;

const formatDateToIso = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export function AnalyticsReportV2() {
  const intl = useIntl();
  const intlRef = useRef(intl);
  intlRef.current = intl;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authInfo } = useContext(AuthContext);

  const [startDate, setStartDate] = useState<string>(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return formatDateToIso(start);
  });
  const [endDate, setEndDate] = useState<string>(() => formatDateToIso(new Date()));
  const [selectedMetric, setSelectedMetric] = useState("video_statistics");
  const [selectedEstablishments, setSelectedEstablishments] = useState<Establishment[]>([]);
  const [format, setFormat] = useState<"table" | "chart">("table");
  const [granularity, setGranularity] = useState<"daily" | "monthly" | "total">("monthly");
  const [separateByChannel, setSeparateByChannel] = useState<"yes" | "no">("no");
  
  const [metricsData, setMetricsData] = useState<Record<string, MetricInfo>>({});

  const [reportData, setReportData] = useState<ReportData | undefined>();
  const [transformedTableData, setTransformedTableData] = useState<any[]>([]);
  const [reportParams, setReportParams] = useState<{
    metric: string;
    startDate: string;
    endDate: string;
    granularity: string;
    hasChannelMetric: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chartParamsState, setChartParamsState] = useState<{
    metric: string;
    granularity: "daily" | "monthly";
    startDate: string;
    endDate: string;
    aggregateTotal: boolean;
  } | null>(null);

  const [reportFormat, setReportFormat] = useState<"table" | "chart">("table");
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await api.get("/app/analytics/metrics-type");
        setMetricsData(response.data);
      } catch (error) {
        toast.error(intlRef.current.formatMessage({ id: 'messages.loadError' }));
      }
    };
    fetchMetrics();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMetric) {
      setError(intl.formatMessage({ id: 'analyticsV2.selectMetric' }));
      return;
    }
    
    const isEstablishmentRequired = separateByChannel === "yes" && hasOperationalAccess(authInfo?.role);
    if (isEstablishmentRequired && selectedEstablishments.length === 0) {
      setError(intl.formatMessage({ id: 'analyticsV2.selectEstablishment' }));
      return;
    }

    setError("");
    setReportFormat(format);
    
    const startIso = startDate;
    const endIso = endDate;

    if (format === "chart") {
      setChartParamsState({
        metric: selectedMetric,
        granularity: granularity as "daily" | "monthly",
        startDate: startIso,
        endDate: endIso,
        aggregateTotal: separateByChannel === "no"
      });
      setReportData(undefined);
      setTransformedTableData([]);
      setReportParams(null);
      return;
    }

    setLoading(true);

    try {
      const params: any = {
        start: startIso,
        end: endIso,
        metric: selectedMetric,
        granularity,
        aggregate_total: separateByChannel === "no"
      };

      if (establishmentIds && establishmentIds.length > 0) {
        params.establishment_ids = establishmentIds.join(",");
      }
      const response = await api.get(`/app/analytics/report`, { params });

      setReportData(response.data);
      setReportParams({
        metric: selectedMetric,
        startDate: startIso,
        endDate: endIso,
        granularity,
        hasChannelMetric: !!hasChannelMetric
      });
      
      const tableRows: any[] = [];
      response.data.forEach((item: any) => {
        const dates = item.data[0];
        const values = item.data[1];
        dates.forEach((date: string, index: number) => {
          if (values[index] > 0) {
            tableRows.push({
              establishmentName: item.establishmentName,
              channelName: item.channelName,
              counts: values[index],
              date: date
            });
          }
        });
      });
      setTransformedTableData(tableRows);
    } catch (error) {
      setError(intl.formatMessage({ id: 'analyticsV2.reportError' }));
    } finally {
      setLoading(false);
    }
  };

  const generateSerieLabel = useCallback((item: any) => {
    return item.establishmentName ? `${item.establishmentName} - ${item.channelName}` : "Total";
  }, []);

  const establishmentIds = useMemo(() => 
    selectedEstablishments.map(e => e.establishmentId), [selectedEstablishments]);

  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }, [sortBy, sortOrder]);

  const sortedMetrics = useMemo(() => 
    Object.keys(metricsData).sort((a, b) => 
      intl.formatMessage({ id: `metrics.${a}` }).localeCompare(intl.formatMessage({ id: `metrics.${b}` }))
    ), [metricsData, intl]);

  const hasChannelMetric = selectedMetric && metricsData[selectedMetric]?.hasChannel;

  return (
    <Container>
      <PageHeader
        title={intl.formatMessage({ id: 'analyticsV2.title' })}
        sx={{ mt: 4 }}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
          mt: 2,
        }}
      >
        <DatePickerField
          label={intl.formatMessage({ id: 'analyticsV2.startDate' })}
          value={startDate}
          onChange={setStartDate}
          size="small"
          fullWidth
        />
        <DatePickerField
          label={intl.formatMessage({ id: 'analyticsV2.endDate' })}
          value={endDate}
          onChange={setEndDate}
          size="small"
          fullWidth
        />

        <Input
          label={intl.formatMessage({ id: 'analyticsV2.metric' })}
          select
          value={selectedMetric}
          onChange={(e) => setSelectedMetric(e.target.value)}
        >
          {sortedMetrics.map((metric) => (
            <MenuItem key={metric} value={metric}>
              {intl.formatMessage({ id: `metrics.${metric}` })}
            </MenuItem>
          ))}
        </Input>

        <Input
          label={intl.formatMessage({ id: 'analyticsV2.format' })}
          select
          value={format}
          onChange={(e) => {
            const newFormat = e.target.value as "table" | "chart";
            setFormat(newFormat);
            if (granularity === "total") {
              setGranularity("daily");
            }
          }}
        >
          <MenuItem value="table">{intl.formatMessage({ id: 'analyticsV2.table' })}</MenuItem>
          <MenuItem value="chart">{intl.formatMessage({ id: 'analyticsV2.chart' })}</MenuItem>
        </Input>

        {hasChannelMetric && (
          <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
            <EstablishmentAutocomplete
              value={selectedEstablishments}
              onChange={setSelectedEstablishments}
              searchOnMount={authInfo?.role === EUserRole.OWNER}
            />
          </Box>
        )}

        <Input
          label={intl.formatMessage({ id: 'analyticsV2.granularity' })}
          select
          value={granularity}
          onChange={(e) => setGranularity(e.target.value as "daily" | "monthly" | "total")}
        >
          <MenuItem value="daily">{intl.formatMessage({ id: 'analyticsV2.daily' })}</MenuItem>
          <MenuItem value="monthly">{intl.formatMessage({ id: 'analyticsV2.monthly' })}</MenuItem>
          {format !== "chart" && <MenuItem value="total">{intl.formatMessage({ id: 'analyticsV2.total' })}</MenuItem>}
        </Input>

        {hasChannelMetric && (
          <Input
            label={intl.formatMessage({ id: 'analyticsV2.groupBy' })}
            select
            value={separateByChannel}
            onChange={(e) => setSeparateByChannel(e.target.value as "yes" | "no")}
          >
            <MenuItem value="no">{intl.formatMessage({ id: 'analyticsV2.all' })}</MenuItem>
            <MenuItem value="yes">{intl.formatMessage({ id: 'analyticsV2.court' })}</MenuItem>
          </Input>
        )}

        {error && (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
          <LoadingButton type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : intl.formatMessage({ id: 'analyticsV2.generateReport' })}
          </LoadingButton>
        </Box>
      </Box>

      {(reportData || (reportFormat === "chart" && chartParamsState)) && (
        <>
          {reportFormat === "chart" && chartParamsState ? (
            <Grid2 container spacing={2} sx={{ mt: 2 }}>
              <Grid2 size={12}>
                <MetricChartCard
                  title={intl.formatMessage({ id: `metrics.${chartParamsState.metric}` })}
                  metricName={chartParamsState.metric}
                  granularity={chartParamsState.granularity}
                  startDate={chartParamsState.startDate}
                  endDate={chartParamsState.endDate}
                  serieLabelGenerator={generateSerieLabel}
                  vertical={isMobile}
                  aggregateTotal={chartParamsState.aggregateTotal}
                  establishmentIds={establishmentIds}
                />
              </Grid2>
            </Grid2>
          ) : reportFormat === "chart" && !chartParamsState ? (
            <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body1">
                {intl.formatMessage({ id: 'analyticsV2.chartNotAvailable' })}
              </Typography>
            </Box>
          ) : reportData && Array.isArray(reportData) && reportData.length > 0 ? (
            <AnalyticsTable
              data={transformedTableData}
              reportParams={reportParams}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              generateSerieLabel={generateSerieLabel}
            />
          ) : (
            <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body1">
                {intl.formatMessage({ id: 'analyticsV2.noDataFound' })}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Container>
  );
}
